import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/contexts/AuthContext';
import { useTranslation } from '@/contexts/I18nContext';
import { AlertTriangle, LogOut, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { router, AppRoute } from '@/lib/router';

export const DangerZoneSection: React.FC = () => {
  const { t } = useTranslation();
  const { logout, deleteAccount } = useAuth();
  const [confirmDeleteVisible, setConfirmDeleteVisible] = useState(false);
  const [confirmLogoutVisible, setConfirmLogoutVisible] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleLogoutAllDevices = async () => {
    setIsLoading(true);
    try {
      setConfirmLogoutVisible(false);
      toast.success(t('security.logoutSuccess'));

      // 🔑 调用logout，Auth0会自动处理重定向
      await logout();
    } catch (error) {
      toast.error(t('security.logoutFailed'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirmText !== 'DELETE') {
      toast.error(t('security.deleteConfirmError'));
      return;
    }

    setIsLoading(true);
    try {
      // 调用真实的API删除账户
      await deleteAccount();
      setConfirmDeleteVisible(false);
      router.navigate(AppRoute.LOGIN);
    } catch (error) {
      // 错误处理已经在 AuthContext 中完成
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Card className="border-red-200">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-red-600">
          <AlertTriangle className="h-5 w-5" />
          {t('settings.dangerZone')}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* 登出所有设备 */}
        <div className="flex items-center justify-between p-4 border border-orange-200 rounded-lg bg-orange-50">
          <div className="space-y-1">
            <h3 className="font-medium text-orange-800">{t('security.logoutAllDevices')}</h3>
            <p className="text-sm text-orange-700">
              {t('security.logoutAllDevicesDesc')}
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setConfirmLogoutVisible(true)}
            className="border-orange-300 text-orange-700 hover:bg-orange-100"
          >
            <LogOut className="h-4 w-4 mr-2" />
            {t('security.logout')}
          </Button>
        </div>

        {/* 删除账户 */}
        <div className="flex items-center justify-between p-4 border border-red-200 rounded-lg bg-red-50">
          <div className="space-y-1">
            <h3 className="font-medium text-red-800">{t('security.deleteAccount')}</h3>
            <p className="text-sm text-red-700">
              {t('security.deleteAccountDesc')}
            </p>
          </div>
          <Button
            className='text-white'
            variant="destructive"
            size="sm"
            onClick={() => setConfirmDeleteVisible(true)}
          >
            <Trash2 className="h-4 w-4 mr-2" />
            {t('security.delete')}
          </Button>
        </div>
      </CardContent>

      {/* 登出确认对话框 */}
      <Dialog open={confirmLogoutVisible} onOpenChange={setConfirmLogoutVisible}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <LogOut className="h-5 w-5 text-orange-500" />
              {t('security.confirmLogout')}
            </DialogTitle>
            <DialogDescription>
              {t('security.confirmLogoutDesc')}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmLogoutVisible(false)}>
              {t('common.cancel')}
            </Button>
            <Button
              className='text-white'
              variant="destructive"
              onClick={handleLogoutAllDevices}
              disabled={isLoading}
            >
              {isLoading ? t('common.processing') : t('security.confirmLogout')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 删除账户确认对话框 */}
      <Dialog open={confirmDeleteVisible} onOpenChange={setConfirmDeleteVisible}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="h-5 w-5" />
              {t('security.confirmDelete')}
            </DialogTitle>
            <DialogDescription>
              {t('security.confirmDeleteDesc')}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-800 font-medium">
                {t('security.deleteWarning')}
              </p>
              <ul className="text-xs text-red-700 mt-2 space-y-1">
                <li>• {t('security.deleteWarning1')}</li>
                <li>• {t('security.deleteWarning2')}</li>
                <li>• {t('security.deleteWarning3')}</li>
              </ul>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="deleteConfirm">
                {t('security.deleteConfirmLabel')} <code className="bg-gray-100 px-1 rounded">DELETE</code>
              </Label>
              <Input
                id="deleteConfirm"
                value={deleteConfirmText}
                onChange={(e) => setDeleteConfirmText(e.target.value)}
                placeholder="DELETE"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDeleteVisible(false)}>
              {t('common.cancel')}
            </Button>
            <Button
              className='text-white'
              variant="destructive"
              onClick={handleDeleteAccount}
              disabled={isLoading || deleteConfirmText !== 'DELETE'}
            >
              {isLoading ? t('common.processing') : t('security.deleteAccount')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};
